import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Divider,
  Checkbox,
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  Search as LoadIcon,
  TrendingDown as DepreciationIcon,
  CheckCircle as SuccessIcon,
  ErrorOutline as ErrorIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import { useConfirm } from '../../hooks/useConfirm';
import { fixedAssetsService } from '../../services/fixedAssets.service';
import {
  PendingDepreciationItem,
  BatchPostResult,
} from '../../types/fixedAssets.types';

// ─── Translations ─────────────────────────────────────────────────────────────
const translations = {
  en: {
    title: 'Depreciation Run',
    subtitle: 'Select a period and post depreciation for multiple assets at once',
    periodLabel: 'Period',
    year: 'Year',
    month: 'Month',
    load: 'Load',
    loading: 'Loading...',
    postSelected: 'Post Selected',
    posting: 'Posting...',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    colAssetCode: 'Asset Code',
    colAssetName: 'Asset Name',
    colCategory: 'Category',
    colDepAmount: 'Depreciation (KWD)',
    colBookValue: 'Current Book Value',
    colExpenseAccount: 'Expense Account',
    colStatus: 'Status',
    noData: 'No pending depreciation found for this period.',
    noDataHint: 'All assets may already be posted or there are no active assets for this period.',
    selected: 'asset(s) selected',
    totalDep: 'Total Depreciation',
    kwd: 'KWD',
    confirmTitle: 'Confirm Depreciation Run',
    confirmMsg: (count: number, total: string) =>
      `Post depreciation for ${count} asset(s)? Total amount: ${total} KWD. This will create journal entries that cannot be easily reversed.`,
    resultsTitle: 'Posting Results',
    totalRequested: 'Requested',
    totalPosted: 'Posted',
    totalFailed: 'Failed',
    close: 'Close Results',
    allPosted: 'All depreciation entries posted successfully.',
    someFailed: 'Some entries failed to post. Review the errors below.',
    errorLoad: 'Failed to load pending depreciation. Please try again.',
    errorPost: 'Posting failed. Please try again.',
    months: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ],
    rowPosted: 'Posted',
    rowFailed: 'Failed',
  },
  ar: {
    title: 'تشغيل الإهلاك',
    subtitle: 'اختر الفترة ورحّل الإهلاك لأصول متعددة في وقت واحد',
    periodLabel: 'الفترة',
    year: 'السنة',
    month: 'الشهر',
    load: 'تحميل',
    loading: 'جاري التحميل...',
    postSelected: 'ترحيل المحدد',
    posting: 'جاري الترحيل...',
    selectAll: 'تحديد الكل',
    deselectAll: 'إلغاء التحديد',
    colAssetCode: 'كود الأصل',
    colAssetName: 'اسم الأصل',
    colCategory: 'الفئة',
    colDepAmount: 'قسط الإهلاك (د.ك)',
    colBookValue: 'القيمة الدفترية',
    colExpenseAccount: 'حساب المصروف',
    colStatus: 'الحالة',
    noData: 'لا يوجد إهلاك معلق لهذه الفترة.',
    noDataHint: 'ربما تم ترحيل جميع الأصول بالفعل أو لا توجد أصول نشطة لهذه الفترة.',
    selected: 'أصل محدد',
    totalDep: 'إجمالي الإهلاك',
    kwd: 'د.ك',
    confirmTitle: 'تأكيد تشغيل الإهلاك',
    confirmMsg: (count: number, total: string) =>
      `ترحيل الإهلاك لـ ${count} أصل؟ الإجمالي: ${total} د.ك. سيتم إنشاء قيود يومية لا يمكن التراجع عنها بسهولة.`,
    resultsTitle: 'نتائج الترحيل',
    totalRequested: 'المطلوب',
    totalPosted: 'تم الترحيل',
    totalFailed: 'فشل',
    close: 'إغلاق النتائج',
    allPosted: 'تم ترحيل جميع قيود الإهلاك بنجاح.',
    someFailed: 'فشل ترحيل بعض القيود. راجع الأخطاء أدناه.',
    errorLoad: 'فشل تحميل الإهلاك المعلق. يرجى المحاولة مرة أخرى.',
    errorPost: 'فشل الترحيل. يرجى المحاولة مرة أخرى.',
    months: [
      'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
      'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
    ],
    rowPosted: 'مرحّل',
    rowFailed: 'فشل',
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmt(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

// ─── Row Result State ──────────────────────────────────────────────────────────
interface RowResultState {
  success: boolean;
  journalEntryNumber?: string;
  error?: string;
}

// ─── Page ─────────────────────────────────────────────────────────────────────
const DepreciationRunPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const now = new Date();
  const [year, setYear] = useState<number>(now.getFullYear());
  const [month, setMonth] = useState<number>(now.getMonth() + 1); // 1-based

  const [items, setItems] = useState<PendingDepreciationItem[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [posting, setPosting] = useState(false);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [rowResults, setRowResults] = useState<Record<number, RowResultState>>({});
  const [batchResult, setBatchResult] = useState<BatchPostResult | null>(null);

  // ── Load pending depreciation ──
  const handleLoad = async () => {
    setLoadingData(true);
    setHasLoaded(false);
    setSelectedIds(new Set());
    setRowResults({});
    setBatchResult(null);
    try {
      const data = await fixedAssetsService.getPendingDepreciation(year, month);
      setItems(data);
      setHasLoaded(true);
    } catch {
      toast.error(t.errorLoad);
    } finally {
      setLoadingData(false);
    }
  };

  // ── Select / deselect ──
  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < items.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(i => i.fixedAssetId)));
    }
  };

  const toggleRow = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Selected totals ──
  const selectedItems = items.filter(i => selectedIds.has(i.fixedAssetId));
  const totalSelectedDep = selectedItems.reduce((sum, i) => sum + i.depreciationAmount, 0);

  // ── Post selected ──
  const handlePostSelected = async () => {
    if (selectedIds.size === 0) return;

    const confirmed = await confirm({
      title: t.confirmTitle,
      message: t.confirmMsg(selectedIds.size, fmt(totalSelectedDep)),
      severity: 'warning',
      confirmText: t.postSelected,
    });
    if (!confirmed) return;

    setPosting(true);
    try {
      const result = await fixedAssetsService.postBatch({
        year,
        month,
        fixedAssetIds: Array.from(selectedIds),
      });

      // Map results onto rows
      const newRowResults: Record<number, RowResultState> = {};
      for (const r of result.results) {
        newRowResults[r.fixedAssetId] = {
          success: r.success,
          journalEntryNumber: r.journalEntryNumber,
          error: r.error,
        };
      }
      setRowResults(newRowResults);
      setBatchResult(result);

      if (result.totalFailed === 0) {
        toast.success(t.allPosted);
      } else {
        toast.warning(t.someFailed);
      }

      // Remove successfully posted items from the list & selection
      const failedIds = new Set(
        result.results.filter(r => !r.success).map(r => r.fixedAssetId)
      );
      setItems(prev => prev.filter(i => failedIds.has(i.fixedAssetId)));
      setSelectedIds(new Set(Array.from(selectedIds).filter(id => failedIds.has(id))));
    } catch {
      toast.error(t.errorPost);
    } finally {
      setPosting(false);
    }
  };

  // ── Render ──
  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* ── Gradient Header ── */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3,
          p: 3,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DepreciationIcon sx={{ fontSize: 44, opacity: 0.85 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">{t.title}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.25 }}>{t.subtitle}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* ── Period Selector ── */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" color="#667eea" gutterBottom>
          {t.periodLabel}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {/* Year */}
          <TextField
            label={t.year}
            type="number"
            value={year}
            onChange={e => setYear(Number(e.target.value))}
            size="small"
            sx={{ width: 120 }}
            inputProps={{ min: 2000, max: 2100 }}
          />

          {/* Month */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>{t.month}</InputLabel>
            <Select
              value={month}
              label={t.month}
              onChange={e => setMonth(Number(e.target.value))}
            >
              {t.months.map((name, idx) => (
                <MenuItem key={idx + 1} value={idx + 1}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Load Button */}
          <Button
            variant="contained"
            onClick={handleLoad}
            disabled={loadingData}
            startIcon={loadingData ? <CircularProgress size={16} color="inherit" /> : <LoadIcon />}
            sx={{
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              boxShadow: '0 3px 5px 2px rgba(102,126,234,.3)',
              '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' },
            }}
          >
            {loadingData ? t.loading : t.load}
          </Button>
        </Box>
      </Paper>

      {/* ── Batch Results Summary ── */}
      {batchResult && (
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid', borderColor: batchResult.totalFailed > 0 ? 'warning.light' : 'success.light' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <DoneAllIcon sx={{ color: batchResult.totalFailed > 0 ? 'warning.main' : 'success.main', fontSize: 32 }} />
              <Typography variant="h6" fontWeight="bold">
                {t.resultsTitle}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => setBatchResult(null)}
            >
              {t.close}
            </Button>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">{t.totalRequested}</Typography>
              <Typography variant="h5" fontWeight="bold">{batchResult.totalRequested}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">{t.totalPosted}</Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">{batchResult.totalPosted}</Typography>
            </Box>
            {batchResult.totalFailed > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">{t.totalFailed}</Typography>
                <Typography variant="h5" fontWeight="bold" color="error.main">{batchResult.totalFailed}</Typography>
              </Box>
            )}
          </Stack>
          {batchResult.totalFailed === 0 ? (
            <Alert severity="success" sx={{ mt: 2 }}>{t.allPosted}</Alert>
          ) : (
            <Alert severity="warning" sx={{ mt: 2 }}>{t.someFailed}</Alert>
          )}
        </Paper>
      )}

      {/* ── Results Table ── */}
      {hasLoaded && (
        <>
          {items.length === 0 ? (
            <Paper elevation={3} sx={{ p: 6, borderRadius: 2, textAlign: 'center' }}>
              <DepreciationIcon sx={{ fontSize: 72, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t.noData}
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {t.noDataHint}
              </Typography>
            </Paper>
          ) : (
            <>
              {/* Toolbar: select all + summary */}
              <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    startIcon={allSelected ? <DeselectIcon /> : <SelectAllIcon />}
                    onClick={toggleSelectAll}
                    variant="outlined"
                    color="primary"
                  >
                    {allSelected ? t.deselectAll : t.selectAll}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {selectedIds.size} {t.selected}
                  </Typography>
                  {selectedIds.size > 0 && (
                    <>
                      <Divider orientation="vertical" flexItem />
                      <Typography variant="body2" fontWeight={600}>
                        {t.totalDep}: {fmt(totalSelectedDep)} {t.kwd}
                      </Typography>
                    </>
                  )}
                </Stack>

                <Button
                  variant="contained"
                  disabled={selectedIds.size === 0 || posting}
                  onClick={handlePostSelected}
                  startIcon={posting ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
                  sx={{
                    background: selectedIds.size === 0 || posting
                      ? undefined
                      : 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                    boxShadow: selectedIds.size > 0 && !posting
                      ? '0 3px 5px 2px rgba(102,126,234,.3)'
                      : undefined,
                    '&:hover': {
                      background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)',
                    },
                  }}
                >
                  {posting ? t.posting : `${t.postSelected} (${selectedIds.size})`}
                </Button>
              </Paper>

              {/* Table */}
              <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                      {/* Select All Checkbox */}
                      <TableCell padding="checkbox" sx={{ bgcolor: 'transparent' }}>
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={toggleSelectAll}
                          sx={{ color: 'rgba(255,255,255,0.8)', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
                        />
                      </TableCell>
                      {[
                        t.colAssetCode,
                        t.colAssetName,
                        t.colCategory,
                        t.colDepAmount,
                        t.colBookValue,
                        t.colExpenseAccount,
                        t.colStatus,
                      ].map(col => (
                        <TableCell key={col} sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map(item => {
                      const isChecked = selectedIds.has(item.fixedAssetId);
                      const rowResult = rowResults[item.fixedAssetId];
                      const hasFailed = rowResult && !rowResult.success;

                      return (
                        <TableRow
                          key={item.fixedAssetId}
                          hover
                          selected={isChecked}
                          onClick={() => toggleRow(item.fixedAssetId)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'rgba(102,126,234,0.05)' },
                            '&.Mui-selected': { bgcolor: 'rgba(102,126,234,0.08)' },
                            '&.Mui-selected:hover': { bgcolor: 'rgba(102,126,234,0.12)' },
                            ...(hasFailed ? { bgcolor: 'rgba(211,47,47,0.04)' } : {}),
                          }}
                        >
                          {/* Checkbox */}
                          <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                            <Checkbox
                              checked={isChecked}
                              onChange={() => toggleRow(item.fixedAssetId)}
                              sx={{ '&.Mui-checked': { color: '#667eea' } }}
                            />
                          </TableCell>

                          {/* Asset Code */}
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold" color="#667eea">
                              {item.assetCode}
                            </Typography>
                          </TableCell>

                          {/* Asset Name */}
                          <TableCell>
                            <Tooltip title={item.assetNameAr}>
                              <Typography variant="body2">
                                {isRTL ? item.assetNameAr : item.assetName}
                              </Typography>
                            </Tooltip>
                            {/* Error message in red */}
                            {hasFailed && (
                              <Typography variant="caption" color="error.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <ErrorIcon sx={{ fontSize: 12 }} />
                                {rowResult.error}
                              </Typography>
                            )}
                          </TableCell>

                          {/* Category */}
                          <TableCell>
                            {item.categoryName ? (
                              <Chip label={item.categoryName} size="small" variant="outlined" color="primary" />
                            ) : (
                              <Typography variant="body2" color="text.disabled">—</Typography>
                            )}
                          </TableCell>

                          {/* Depreciation Amount */}
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="error.main">
                              {fmt(item.depreciationAmount)}
                            </Typography>
                          </TableCell>

                          {/* Current Book Value */}
                          <TableCell align="right">
                            <Typography variant="body2">
                              {fmt(item.currentBookValue)}
                            </Typography>
                          </TableCell>

                          {/* Expense Account */}
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                              {item.depExpenseAccountName || '—'}
                            </Typography>
                          </TableCell>

                          {/* Row Status (from batch result if available) */}
                          <TableCell>
                            {rowResult ? (
                              rowResult.success ? (
                                <Chip
                                  label={t.rowPosted}
                                  color="success"
                                  size="small"
                                  icon={<SuccessIcon />}
                                  sx={{ fontWeight: 'bold' }}
                                />
                              ) : (
                                <Chip
                                  label={t.rowFailed}
                                  color="error"
                                  size="small"
                                  icon={<ErrorIcon />}
                                  sx={{ fontWeight: 'bold' }}
                                />
                              )
                            ) : (
                              <Typography variant="body2" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Footer Summary Card */}
              <Card
                elevation={3}
                sx={{
                  mt: 2,
                  background: selectedIds.size > 0
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : undefined,
                  color: selectedIds.size > 0 ? 'white' : undefined,
                  borderRadius: 2,
                  transition: 'all 0.3s',
                }}
              >
                <CardContent sx={{ py: '12px !important' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="body1" fontWeight={600}>
                      {selectedIds.size} {t.selected}
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      {t.totalDep}: {fmt(totalSelectedDep)} {t.kwd}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default DepreciationRunPage;
